import axios from 'axios';
import { logApiError } from '../utils/LogErrorsUtil';
import { fetchAuthToken, isAccessTokenValid } from './login';
import { getStore } from '../redux/configureStore';
axios.defaults.baseURL = process.env.REACT_APP_API_PATH;

/**
 * Gets agency from Redux store state (reads directly from store, not localStorage)
 * This ensures we get the latest value even if redux-persist hasn't written to localStorage yet
 * @returns {string|null} - The agency value or null if not found
 */
const getAgencyFromState = () => {
  try {
    const store = getStore();
    if (store) {
      const state = store.getState();
      const dashboardStats = state?.dashboardStats;
      const userProfile = state?.userProfile;
      
      // Priority: selectedAgency from dashboardStats, then userData.agency from userProfile
      if (dashboardStats?.selectedAgency) {
        return dashboardStats.selectedAgency;
      }
      if (userProfile?.userData?.agency) {
        return userProfile.userData.agency;
      }
    }
  } catch (error) {
    console.error('Error getting agency from store:', error);
  }
  
  // Fallback to localStorage if store is not available
  try {
    const persistedState = localStorage.getItem('persist:root');
    if (persistedState) {
      const state = JSON.parse(persistedState);
      const dashboardStats = state.dashboardStats ? JSON.parse(state.dashboardStats) : null;
      const userProfile = state.userProfile ? JSON.parse(state.userProfile) : null;
      
      if (dashboardStats?.selectedAgency) {
        return dashboardStats.selectedAgency;
      }
      if (userProfile?.userData?.agency) {
        return userProfile.userData.agency;
      }
    }
  } catch (fallbackError) {
    console.error('Error getting agency from localStorage fallback:', fallbackError);
  }
  
  return null;
};

/**
 * Adds agency to URL if it's not already present and the URL needs it
 * @param {string} url - The request URL
 * @returns {string} - The URL with agency parameter added if needed
 */
const addAgencyToUrl = (url) => {
  const agency = getAgencyFromState();
  if (!agency) {
    return url;
  }

  // Check if URL already has agency parameter
  if (url.includes('agency=')) {
    return url;
  }

  // Check if this is a dashboard, reporting, or support endpoint that needs agency
  const needsAgency = url.includes('/dashboard/') || 
                      url.includes('/support/') || 
                      url.includes('/report/');
  
  if (!needsAgency) {
    return url;
  }

  // Add agency to URL
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}agency=${encodeURIComponent(agency)}`;
};

/**
 * Adds agency to request data if it's a POST request and data is an object
 * @param {object} request - The request object
 */
const addAgencyToData = (request) => {
  const agency = getAgencyFromState();
  if (!agency || request.method?.toLowerCase() !== 'post') {
    return;
  }

  // Only add to data if data is an object and doesn't already have agency
  if (request.data && typeof request.data === 'object' && !Array.isArray(request.data)) {
    if (!request.data.agency) {
      request.data = { ...request.data, agency };
    }
  }
};

const authBaseHttpRequest = (request, needToken = true) => {
  return new Promise(async (resolve, reject) => {
    let { isTokenValid, access_token, refresh_token } = await isAccessTokenValid();
    if (needToken) {
      const needsRefresh = !isTokenValid && refresh_token;
      if (needsRefresh) {
        const tokenValues = await fetchAuthToken({ refresh_token });
        if (tokenValues?.success && tokenValues.access_token) {
          access_token = tokenValues.access_token;
        } else {
          const err = new Error('Session expired. Please sign in again.');
          if (tokenValues?.error) {
            err.response = tokenValues.error;
          }
          reject(err);
          return;
        }
      }

      if (!access_token) {
        reject(new Error('Not authenticated.'));
        return;
      }

      const { headers = {} } = request;
      request.headers = { ...headers, Authorization: `Bearer ${access_token}` };
    }

    // Automatically add agency to URL if needed
    if (request.url) {
      request.url = addAgencyToUrl(request.url);
    }

    // Automatically add agency to data if needed (for POST requests)
    addAgencyToData(request);

    axios(request)
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        logApiError(error);
        reject(error);
      });
  });
};

export default authBaseHttpRequest;
